/* Home: Testimonials.
   Desktop (>=990px) renders the reviews as a static 2x2 grid — see
   section-home-testimonials.css — so Swiper is only mounted below that width
   and destroyed again above it. On desktop the arrows page through the grid
   four reviews at a time; on mobile they drive the Swiper track (327px cards
   at a 24px gap, one per view).

   Each element keeps a reference to its own media-query listener so repeated
   `shopify:section:load` events in the theme editor replace it instead of
   stacking up a new one every save. */
const HOME_TESTIMONIALS_QUERY = '(max-width: 989px)';
const HOME_TESTIMONIALS_PAGE_SIZE = 4;
const homeTestimonialsListeners = new WeakMap();

function getHomeTestimonialsArrows(el) {
  const section = el.closest('.home-testimonials');
  return {
    prev: section && section.querySelector('[data-testimonials-prev]'),
    next: section && section.querySelector('[data-testimonials-next]'),
  };
}

/* Desktop grid paging: show one page of four slides and disable the arrows at
   either end (both stay disabled when every review fits on one page). */
function renderHomeTestimonialsPage(el) {
  const slides = el.querySelectorAll('.swiper-slide');
  const pages = Math.max(1, Math.ceil(slides.length / HOME_TESTIMONIALS_PAGE_SIZE));
  const page = Math.min(Math.max(Number(el.dataset.page) || 0, 0), pages - 1);
  el.dataset.page = page;

  slides.forEach((slide, index) => {
    slide.hidden = Math.floor(index / HOME_TESTIMONIALS_PAGE_SIZE) !== page;
  });

  const { prev, next } = getHomeTestimonialsArrows(el);
  if (prev) prev.disabled = page === 0;
  if (next) next.disabled = page === pages - 1;
}

function clearHomeTestimonialsPage(el) {
  el.querySelectorAll('.swiper-slide').forEach((slide) => {
    slide.hidden = false;
  });
  const { prev, next } = getHomeTestimonialsArrows(el);
  if (prev) prev.disabled = false;
  if (next) next.disabled = false;
}

function syncHomeTestimonialsSlider(el, mediaQuery) {
  if (typeof Swiper === 'undefined') return;

  if (mediaQuery.matches && !el.swiper) {
    clearHomeTestimonialsPage(el);
    const { prev, next } = getHomeTestimonialsArrows(el);
    new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 24,
      navigation: { prevEl: prev, nextEl: next },
    });
  } else if (!mediaQuery.matches) {
    if (el.swiper) el.swiper.destroy(true, true);
    renderHomeTestimonialsPage(el);
  }
}

function teardownHomeTestimonialsSlider(el) {
  const previous = homeTestimonialsListeners.get(el);
  if (previous) {
    previous.mediaQuery.removeEventListener('change', previous.handler);
    const { prev, next } = getHomeTestimonialsArrows(el);
    if (prev) prev.removeEventListener('click', previous.onPrev);
    if (next) next.removeEventListener('click', previous.onNext);
    homeTestimonialsListeners.delete(el);
  }
  if (el.swiper) el.swiper.destroy(true, true);
}

function initHomeTestimonialsSliders(root) {
  if (typeof Swiper === 'undefined') return;

  (root || document).querySelectorAll('[data-slider="home-testimonials"]').forEach((el) => {
    teardownHomeTestimonialsSlider(el);

    const mediaQuery = window.matchMedia(HOME_TESTIMONIALS_QUERY);
    const handler = () => syncHomeTestimonialsSlider(el, mediaQuery);
    const step = (delta) => () => {
      if (mediaQuery.matches) return; // Swiper handles the arrows on mobile
      el.dataset.page = (Number(el.dataset.page) || 0) + delta;
      renderHomeTestimonialsPage(el);
    };
    const onPrev = step(-1);
    const onNext = step(1);

    const { prev, next } = getHomeTestimonialsArrows(el);
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    mediaQuery.addEventListener('change', handler);
    homeTestimonialsListeners.set(el, { mediaQuery, handler, onPrev, onNext });

    syncHomeTestimonialsSlider(el, mediaQuery);
  });
}

function destroyHomeTestimonialsSliders(root) {
  (root || document).querySelectorAll('[data-slider="home-testimonials"]').forEach(teardownHomeTestimonialsSlider);
}

initHomeTestimonialsSliders(document);
document.addEventListener('shopify:section:load', (event) => initHomeTestimonialsSliders(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyHomeTestimonialsSliders(event.target));
